import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { formatIDR, formatNumber } from "@/lib/format";
import { Calculator as CalcIcon, ArrowRight, Sparkles } from "lucide-react";

export default function CalculatorPage() {
  const navigate = useNavigate();
  const [stitch, setStitch] = useState(12500);
  const [rate, setRate] = useState(1200);
  const [qty, setQty] = useState(50);
  const [setup, setSetup] = useState(50000);
  const [metallic, setMetallic] = useState(false);
  const [badge, setBadge] = useState(false);
  const [rush, setRush] = useState(false);

  const pricePerPc = (stitch / 1000) * rate;
  const embroiderySubtotal = pricePerPc * qty;
  let addons = 0;
  const breakdown = [];
  if (metallic) { const m = embroiderySubtotal * 0.15; addons += m; breakdown.push(["Benang Metallic (+15%)", m]); }
  if (badge) { const b = qty * 2000; addons += b; breakdown.push([`Potong Badge/Patch (${qty} pcs)`, b]); }
  if (rush) { const r = (embroiderySubtotal + Number(setup)) * 0.2; addons += r; breakdown.push(["Order Kilat / Rush (+20%)", r]); }
  const grandTotal = embroiderySubtotal + Number(setup) + addons;
  const dpRec = grandTotal * 0.5;

  const convert = () => {
    navigate("/order", { state: { prefill: {
      stitch_count: Number(stitch), rate_per_1000: Number(rate), qty: Number(qty),
      setup_fee: Number(setup), addons_fee: Math.round(addons),
      total: Math.round(grandTotal), dp: Math.round(dpRec), payment_status: "DP",
    }}});
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl">Kalkulator Harga Bordir</h1>
        <p className="text-sm text-slate-500">Hitung estimasi harga otomatis berdasarkan jumlah tusukan.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-7 p-6 border-slate-200/80 shadow-sm space-y-5">
          <div className="flex items-center gap-2 text-blue-600"><CalcIcon className="h-5 w-5" /><span className="font-heading font-bold">Parameter Bordir</span></div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Jumlah Stitch / Tusukan</Label>
              <Input data-testid="input-stitch-count" type="number" value={stitch} onChange={(e) => setStitch(+e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Tarif per 1.000 Stitch (Rp)</Label>
              <Input data-testid="input-stitch-rate" type="number" value={rate} onChange={(e) => setRate(+e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Jumlah Qty (pcs)</Label>
              <Input data-testid="input-qty" type="number" value={qty} onChange={(e) => setQty(+e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Biaya Setup / Film / Punching (Rp)</Label>
              <Input data-testid="input-setup-fee" type="number" value={setup} onChange={(e) => setSetup(+e.target.value)} />
            </div>
          </div>

          <div className="space-y-3 pt-2">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Biaya Tambahan</p>
            {[["Benang Metallic (+15%)", metallic, setMetallic, "sw-metallic"],
              ["Potong Badge / Patch (Rp 2.000/pcs)", badge, setBadge, "sw-badge"],
              ["Order Kilat / Rush (+20%)", rush, setRush, "sw-rush"]].map(([label, val, setter, tid]) => (
              <div key={tid} className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
                <span className="text-sm text-slate-700">{label}</span>
                <Switch data-testid={tid} checked={val} onCheckedChange={setter} />
              </div>
            ))}
          </div>
        </Card>

        <Card className="lg:col-span-5 p-6 border-slate-200/80 shadow-sm bg-slate-900 text-white h-fit">
          <div className="flex items-center gap-2 mb-4"><Sparkles className="h-5 w-5 text-blue-400" /><span className="font-heading font-bold">Rincian Harga</span></div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-slate-400">Harga bordir / pcs</span><span data-testid="calc-price-per-pc" className="font-mono">{formatIDR(pricePerPc)}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Subtotal bordir ({qty} pcs)</span><span className="font-mono">{formatIDR(embroiderySubtotal)}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Biaya setup</span><span className="font-mono">{formatIDR(setup)}</span></div>
            {breakdown.map(([l, v]) => (
              <div key={l} className="flex justify-between text-blue-300"><span>{l}</span><span className="font-mono">{formatIDR(v)}</span></div>
            ))}
            <div className="border-t border-slate-700 my-2" />
            <div className="flex justify-between items-center">
              <span className="text-slate-300">Grand Total</span>
              <span data-testid="calc-grand-total" className="font-heading text-2xl font-extrabold text-white">{formatIDR(grandTotal)}</span>
            </div>
            <div className="flex justify-between text-amber-300"><span>DP Minimal (50%)</span><span className="font-mono">{formatIDR(dpRec)}</span></div>
          </div>
          <Button data-testid="btn-convert-to-order" onClick={convert} className="mt-5 w-full bg-blue-600 hover:bg-blue-700 h-11">
            Konversi jadi SPK / Order <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Card>
      </div>
    </div>
  );
}
